import React from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const HERO_IMG = "/hero/lombok-hero-2000.jpg";
const HERO_SRCSET =
  "/hero/lombok-hero-900.jpg 900w, /hero/lombok-hero-1400.jpg 1400w, /hero/lombok-hero-2000.jpg 2000w";

const Hero = () => {
  const { t } = useLang();
  const title = t("hero.title");

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-charcoal"
    >
      {/* Background image with ken burns */}
      <div className="absolute inset-0 kenburns-wrap">
        <img
          src={HERO_IMG}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Aerial view of the Lombok coastline at Mandalika, Indonesia"
          className="w-full h-full object-cover"
          fetchpriority="high"
          decoding="async"
        />
      </div>
      {/* Gradient overlay — bottom-weighted for text legibility over the ocean/land */}
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/25 to-charcoal/85" />
      {/* Left-side darker gradient so left-aligned copy reads clean against land/sky */}
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/55 via-charcoal/10 to-transparent" />
      <div className="absolute inset-0 noise-overlay" />

      {/* Content */}
      <div className="relative z-10 flex min-h-[100svh] flex-col justify-between">
        {/* Spacer for navbar */}
        <div className="h-28" />

        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-10 pb-32 sm:pb-40">
          <div className="max-w-4xl">
            <p
              className="text-[0.72rem] font-medium tracking-[0.22em] uppercase text-ivory/85 fade-up-on-load"
              data-testid="hero-overline"
            >
              {t("hero.overline")}
            </p>
            <h1
              className="mt-6 font-heading font-light text-ivory leading-[1.02] tracking-tight text-5xl sm:text-7xl lg:text-8xl whitespace-pre-line fade-up-on-load delay-100"
              data-testid="hero-title"
            >
              {title}
            </h1>
            <p
              className="mt-8 max-w-2xl text-base sm:text-lg text-ivory/85 font-light leading-relaxed fade-up-on-load delay-200"
              data-testid="hero-subtitle"
            >
              {t("hero.subtitle")}
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-6 fade-up-on-load delay-300">
              <a
                href="#projects"
                data-testid="hero-cta-projects"
                className="group inline-flex items-center gap-3 bg-ivory text-charcoal px-8 py-4 text-[0.78rem] tracking-[0.22em] uppercase font-medium hover:bg-teakwood hover:text-ivory transition-colors duration-500"
              >
                {t("hero.cta_projects")}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-x-1"
                />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 text-ivory/90 text-[0.78rem] tracking-[0.22em] uppercase font-medium border-b border-ivory/60 pb-1 hover:border-ivory transition-all hover:text-ivory"
              >
                {t("hero.cta_contact")}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom editorial bar */}
        <div className="relative z-10 border-t border-ivory/15 bg-charcoal/30 backdrop-blur-sm">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-8 text-ivory/70 text-[0.7rem] tracking-[0.25em] uppercase font-medium">
              <span>Lombok · Indonesia</span>
              <span className="hidden sm:inline-block h-3 w-px bg-ivory/30" />
              <span className="hidden sm:inline">Est. 2024</span>
            </div>
            <div className="text-ivory/60 text-[0.7rem] tracking-[0.25em] uppercase font-medium">
              Scroll ↓
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
